import styled from '@emotion/styled';
import { ThemeProps } from '@theme/base';
import { breakpoints, mq } from '@styles/breakpoints';
import { Icon } from '@components/common/generic-icon';
import { Heading as BaseHeading, ModalContainer } from '@styles/modal';
import { TILE_FALLBACK_IMAGE } from '@mock/fallback';
import { ImageGallery } from '@components/common/image-gallery/ImageGallery';
import { getImageUrl } from '@util/images';
import { Fade } from '@components/common/modal/info-card';
import { TextMarkdown } from '@components/common/description';

/* ***************** *
 *       Types       *
 * ***************** */
export interface PromoBannerModalProps {
  onDismiss: () => void;
  modalData: any;
}

/* ****************** *
 *       Styles       *
 * ****************** */
interface ContainerProps extends ThemeProps {
  hasImage?: boolean;
}

const Container: any = styled.div(({ theme, hasImage }: ContainerProps) => ({
  backgroundColor: theme.custom.colors.white,
  alignItems: '',
  borderRadius: 2,
  display: 'grid',
  gridTemplateColumns: '1fr',

  [mq.small]: {
    maxWidth: 600
  },
  ...(hasImage && {
    [mq.medium]: {
      alignSelf: 'center',
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: '1fr',
      maxWidth: 1000
    }
  })
}));

const ModalImgWrapper: any = styled.div(({ theme }: ThemeProps) => ({
  backgroundColor: theme.custom.colors.white,
  overflow: 'hidden'
}));

const ModalImg = styled.img({
  height: '100%',
  width: '100%'
});

const Heading: any = styled(BaseHeading)(({ theme }: ThemeProps) => ({
  padding: theme.custom.spacing.medium,
  margin: 0,

  [mq.small]: {
    padding: theme.custom.spacing.large
  }
}));

interface PromoTextProps extends ThemeProps {
  hasGallery: boolean;
  centerAlign: boolean;
}

const PromoText: any = styled.div(({ theme, hasGallery, centerAlign }: PromoTextProps) => ({
  padding: theme.custom.spacing.medium,
  ['.promo']: {
    maxHeight: hasGallery ? 225 : 600,
    paddingBottom: theme.custom.spacing.xLarge
  },
  textAlign: centerAlign ? 'center' : 'left',

  [mq.small]: {
    padding: theme.custom.spacing.large,
    paddingBottom: theme.custom.spacing.xLarge
  }
}));

const CloseIcon: any = styled(Icon)({
  cursor: 'pointer',
  fontSize: '2rem'
});

const ModalContent: any = styled.div(({ theme }: ThemeProps) => ({
  paddingBottom: theme.custom.spacing.medium,
  [mq.small]: {
    paddingBottom: 0
  }
}));

export const PromoBannerModal = ({ onDismiss, modalData }: PromoBannerModalProps) => {
  const { title, gallery = [], centreAlignText, heroImage } = modalData; /* , heroImage */
  const isBlackFridaySale = title === 'Black Friday Sale';

  /* *** LOGIC *** */
  const getGalleryImageUrls = () =>
    gallery.map((cmsGalleryItem: any) => ({
      thumbnail: getImageUrl(cmsGalleryItem.fields?.file?.url, breakpoints.small) || TILE_FALLBACK_IMAGE,
      original: getImageUrl(cmsGalleryItem.fields?.file?.url, breakpoints.small) || TILE_FALLBACK_IMAGE
    }));

  return (
    <ModalContainer>
      <Container hasImage={!!heroImage}>
        {heroImage && (
          <ModalImgWrapper>
            <ModalImg src={heroImage || '/assets/images/promo/tour-ppc-discount-400.jpg'} />
          </ModalImgWrapper>
        )}
        <ModalContent>
          <Heading>
            {title}
            <CloseIcon name={'mhi-clear'} onClick={onDismiss} />
          </Heading>
          {!!gallery?.length && <ImageGallery galleryImages={getGalleryImageUrls()} showGalleryDots={true} />}
          <PromoText
            hasGallery={!!gallery?.length}
            centerAlign={centreAlignText}
            classNames={isBlackFridaySale ? 'black-friday' : 'promo'}
          >
            <TextMarkdown description={modalData.markdown} />
          </PromoText>
          {!heroImage && !isBlackFridaySale && <Fade />}
        </ModalContent>
      </Container>
    </ModalContainer>
  );
};
